import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, ShieldCheck, X } from "lucide-react";
import { fetchAdmins, createAdmin, updateAdmin, revokeAdmin, fetchRbacMeta, approveAccessRequest } from "@admin/services/adminApi";
import { AccessRequestsPanel } from "@admin/components/team/AccessRequestsPanel";
import { PageHeader, DataTable, Pill } from "@admin/components/Primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

function PermissionPicker({ permissions, value, onChange, testid }) {
  const toggle = (p) => {
    const next = new Set(value);
    next.has(p) ? next.delete(p) : next.add(p);
    onChange(Array.from(next));
  };
  return (
    <div className="grid grid-cols-2 gap-2" data-testid={testid}>
      {permissions.map((p) => (
        <label key={p} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-xs font-semibold transition ${
          value.includes(p) ? "bg-[#0A2540] border-[#0A2540] text-white" : "bg-white border-slate-200 text-slate-600 hover:border-[#0A2540]"
        }`}>
          <input
            type="checkbox"
            className="hidden"
            checked={value.includes(p)}
            onChange={() => toggle(p)}
            data-testid={`${testid}-${p}`}
          />
          <span>{p}</span>
        </label>
      ))}
    </div>
  );
}

function PromoteForm({ rbacMeta, onClose, onSaved, initial, accessRequest }) {
  const [email, setEmail] = useState(initial?.email || accessRequest?.email || "");
  const [perms, setPerms] = useState(initial?.permissions || []);
  const [roles, setRoles] = useState(initial?.admin_roles || []);
  const [busy, setBusy] = useState(false);
  const isEdit = !!initial?.user_id;

  const togglePreset = (slug) => {
    const next = new Set(roles);
    if (next.has(slug)) next.delete(slug); else next.add(slug);
    setRoles(Array.from(next));
    // Auto-add the preset's perms but allow user to add more
    const preset = rbacMeta.roles.find((r) => r.slug === slug);
    if (preset && !next.has(slug) === false) {  // toggled on
      const merged = new Set([...perms, ...preset.permissions]);
      setPerms(Array.from(merged));
    }
  };

  const save = async () => {
    if (!email && !isEdit) return toast.error("Email required");
    if (!perms.length && !roles.length) return toast.error("Pick at least one permission or role");
    setBusy(true);
    try {
      if (accessRequest) {
        await approveAccessRequest(accessRequest.request_id, { permissions: perms, admin_roles: roles });
        toast.success(`${accessRequest.email} approved as admin`);
      } else if (isEdit) {
        await updateAdmin(initial.user_id, { permissions: perms, admin_roles: roles });
        toast.success("Permissions updated");
      } else {
        await createAdmin({ email: email.trim().toLowerCase(), permissions: perms, admin_roles: roles });
        toast.success(`${email} promoted to admin`);
      }
      onSaved();
    } catch (e) { const d = e?.response?.data?.detail; toast.error((d && d.message) || d || "Save failed"); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4" data-testid="admin-promote-modal" data-mode={accessRequest ? "approve-request" : isEdit ? "edit" : "new"}>
      <div className="bg-white rounded-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>
            {accessRequest ? `Approve access request — ${accessRequest.email}` : isEdit ? `Edit ${initial.email}` : "Promote new admin"}
          </h3>
          <button onClick={onClose} data-testid="admin-promote-close"><X size={18} /></button>
        </div>

        {accessRequest && (
          <div className="mb-4 rounded-xl bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600" data-testid="approve-request-summary">
            <p><span className="font-bold text-[#0A2540]">{accessRequest.name || accessRequest.email}</span> · {accessRequest.email}</p>
            <p className="mt-1 italic">“{accessRequest.reason}”</p>
            <p className="mt-1 text-[11px] text-slate-400">Choose the role presets / permissions to grant. Requires fresh 2FA.</p>
          </div>
        )}
        {!isEdit && !accessRequest && (
          <div className="mb-4">
            <Label htmlFor="email" className="text-xs">Email</Label>
            <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="ops@advoset.in" className="mt-1" data-testid="promote-email" />
            <p className="text-[11px] text-slate-400 mt-1">User must have signed in once already.</p>
          </div>
        )}

        <Label className="text-xs">Role presets</Label>
        <div className="flex flex-wrap gap-2 mt-1 mb-4">
          {rbacMeta.roles.filter((r) => !(accessRequest && r.slug === "super_admin")).map((r) => (
            <button
              key={r.slug}
              onClick={() => togglePreset(r.slug)}
              data-testid={`preset-${r.slug}`}
              className={`px-3 py-1.5 rounded-full text-xs font-bold border transition ${
                roles.includes(r.slug) ? "bg-[#D4AF37] border-[#D4AF37] text-[#0A2540]" : "border-slate-200 text-slate-600 hover:border-[#0A2540]"
              }`}
              title={r.description}
            >
              {r.label}
            </button>
          ))}
        </div>

        <Label className="text-xs">Permissions</Label>
        <div className="mt-1">
          <PermissionPicker testid="perm-picker" permissions={rbacMeta.permissions} value={perms} onChange={setPerms} />
        </div>

        <div className="mt-6 flex gap-2 justify-end">
          <Button variant="outline" onClick={onClose} data-testid="promote-cancel" className="rounded-xl">Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="promote-save" className="rounded-xl adv-btn-primary font-bold">
            <ShieldCheck size={14} /> {accessRequest ? "Approve & grant access" : isEdit ? "Save changes" : "Grant access"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function AdminTeam() {
  const [admins, setAdmins] = useState([]);
  const [rbacMeta, setRbacMeta] = useState(null);
  const [editing, setEditing] = useState(null); // null | "new" | { user_id, email, ... }
  const [approving, setApproving] = useState(null); // access request being approved
  const [requestsKey, setRequestsKey] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [a, m] = await Promise.all([fetchAdmins(), fetchRbacMeta()]);
      setAdmins(a.items || []);
      setRbacMeta(m);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const onRevoke = async (a) => {
    if (!window.confirm(`Revoke admin access for ${a.email}?`)) return;
    try { await revokeAdmin(a.user_id); toast.success("Revoked"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const columns = [
    {
      key: "email",
      label: "Admin",
      render: (r) => (
        <div className="min-w-[220px]">
          <p className="font-bold text-[#0A2540] text-sm">{r.name || r.email}</p>
          <p className="text-[11px] text-slate-500">{r.email}</p>
        </div>
      ),
    },
    {
      key: "admin_roles",
      label: "Role presets",
      render: (r) => (
        <div className="flex flex-wrap gap-1">
          {(r.admin_roles || []).map((s) => <Pill key={s} color="gold">{s}</Pill>)}
          {(!r.admin_roles || r.admin_roles.length === 0) && <span className="text-xs text-slate-400">—</span>}
        </div>
      ),
    },
    {
      key: "permissions",
      label: "Permissions",
      render: (r) => <span className="text-xs text-slate-500">{(r.permissions || []).length} granted</span>,
    },
    {
      key: "actions",
      label: "Actions",
      render: (r) => (
        <div className="flex gap-1">
          <button onClick={() => setEditing(r)} className="px-2 py-1 text-xs font-bold rounded-md border border-slate-200 hover:border-[#0A2540]" data-testid={`edit-admin-${r.user_id}`}>
            Edit
          </button>
          <button onClick={() => onRevoke(r)} className="px-2 py-1 text-xs font-bold rounded-md text-rose-700 border border-rose-200 hover:bg-rose-50" data-testid={`revoke-admin-${r.user_id}`}>
            <Trash2 size={10} className="inline" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        testid="admin-team-header"
        title="Admin Team"
        subtitle="Manage admin accounts and their permissions. Super-admins (env allowlist) cannot be revoked here."
        actions={
          <Button onClick={() => setEditing("new")} data-testid="add-admin-btn" className="rounded-xl adv-btn-gold font-bold">
            <Plus size={14} /> Add admin
          </Button>
        }
      />
      <AccessRequestsPanel refreshKey={requestsKey} onApprove={(r) => setApproving(r)} />
      <DataTable testid="admins-table" columns={columns} rows={admins} empty={loading ? "Loading…" : "No admins"} />
      {editing && rbacMeta && (
        <PromoteForm
          rbacMeta={rbacMeta}
          initial={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); }}
        />
      )}
      {approving && rbacMeta && (
        <PromoteForm
          rbacMeta={rbacMeta}
          accessRequest={approving}
          onClose={() => setApproving(null)}
          onSaved={async () => { setApproving(null); setRequestsKey((k) => k + 1); await load(); }}
        />
      )}
    </>
  );
}
